import AssetInput from '../../../../components/AssetInput';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import { config } from '../../../../config/config';
import { EnabledChains, Tokens } from '../../../../config/constants';
import useUserBalance from '../../../../hooks/web3/useUserBalance';
import { useWeb3React } from '@web3-react/core';
import useTokenDetails from '../../../../hooks/web3/useTokenDetails';
import { formatPercentages, formatUSD } from '../../../../helpers';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { ConversionArrowIcon } from '../../../../assets/icons';
import usePoolState from '../../../../context/pool/usePoolState';
import { preventOverflow } from '../../../../web3/utils';
import { useEffect } from 'react';
import clsx from 'clsx';
import Spinner from '../../../../components/Spinner';

const DepositInterface = () => {
    const { chainId } = useWeb3React();

    const {
        selectedLiquidityPool,
        selectedLockUp,
        depositAmount,
        setSelectedLockUp,
        setDepositAmount,
        resetPoolState,
    } = usePoolState();

    const userBalance = useUserBalance(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setDepositAmount(preventOverflow(value, decimals));
    };

    const handleMax = () =>
        setDepositAmount(
            formatUSD(formatUnits(userBalance || '0', decimals), {
                useGrouping: false,
                currency: '',
                maxDecimals: decimals,
            })
        );

    const feeMultiplier =
        selectedLockUp.feeMultiplier === undefined ? 1 : selectedLockUp.feeMultiplier;

    useEffect(() => () => resetPoolState(), [resetPoolState]);

    return selectedLiquidityPool ? (
        <Card className='deposit-interface'>
            <div className='lock-up-options-container'>
                {config.lockUpOptions.map((lockUp, index) => {
                    const active = selectedLockUp.lockUpId === lockUp.lockUpId;

                    return (
                        <Button
                            key={index}
                            className={clsx('lock-up-option', {
                                active,
                            })}
                            onClick={() => setSelectedLockUp(lockUp)}
                            disabled={active}
                        >
                            {lockUp.duration === undefined
                                ? 'No Lock'
                                : `${lockUp.duration} days`}
                        </Button>
                    );
                })}
            </div>
            <div className='conversion-container'>
                <AssetInput
                    asset={Tokens.USDC}
                    label={
                        <>
                            <span>Balance:</span>&nbsp;
                            <span>
                                {userBalance
                                    ? formatUSD(formatUnits(userBalance, decimals), {
                                          maxDecimals: 2,
                                          currency: '',
                                      })
                                    : '-'}
                                &nbsp; USDC
                            </span>
                        </>
                    }
                    sideLabel={
                        <Button className='max-available' onClick={handleMax}>
                            Max
                        </Button>
                    }
                    name='balance'
                    value={depositAmount}
                    onChange={handleChange}
                />
                <ConversionArrowIcon />
                <AssetInput
                    name='lp-token'
                    value={formatUnits(
                        parseUnits(depositAmount || '0', decimals)
                            .mul(parseUnits('1', decimals))
                            .div(
                                selectedLiquidityPool.isZeroPrice
                                    ? parseUnits('1', decimals)
                                    : parseUnits(
                                          selectedLiquidityPool.currentPriceEstimate,
                                          decimals
                                      )
                            ),
                        decimals
                    )}
                    asset={selectedLiquidityPool.lpToken}
                    label='Receive'
                    readOnly
                />
            </div>
            <div className='conversion-detail'>
                <div className='detail'>
                    <div className='title'>Ratio</div>
                    <div className='value'>
                        {formatUSD(1, { minDecimals: 2, currency: '' })} {Tokens.USDC} ={' '}
                        {formatUSD(selectedLiquidityPool.currentLpPriceEstimate, {
                            minDecimals: 2,
                            currency: '',
                        })}{' '}
                        {selectedLiquidityPool.lpToken}
                    </div>
                </div>
                <div className='detail'>
                    <div className='title'>Multiplier / APR</div>
                    <div className='value'>
                        {feeMultiplier}x /{' '}
                        {formatPercentages(+selectedLiquidityPool.apr * feeMultiplier)}%
                    </div>
                </div>
            </div>
        </Card>
    ) : (
        <Card className='deposit-interface'>
            <div className='loading-container'>
                <Spinner />
            </div>
        </Card>
    );
};

export default DepositInterface;
